import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ProgressBar, Spinner } from "react-bootstrap";
import { Localization, SessionContext, SettingsManager, AppMessagesContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { autoUpdate, flip, offset, shift, useClick, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { floatingBoundaryMiddleware, MITFloatingPortal, useFloatingPortalContext } from "src/componentsUtils/PortalHelpers";
import { ConfirmationDialog } from "src/components/ConfirmationDialog";
import { actionSetMapInteractionState, MapInteractionState, MapitStateContext } from "src/states/MapitState";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { closeWindow, ManagedWindow, openWindow, sendParams, WindowId, WindowState } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { LayerInfo, LayerType } from "src/common/managers/Types";
import { OptimisticLockingConflictError } from "src/common/managers/ESVersionedPersistance";
import { PLADataServiceImpl, PLAProjectServiceImpl } from "./PLAPersistence";
import { PLAElementRecord, PLAProject, PLARecord, PLARelationRecord, PLALocalStorageSelectedProjectKey, PLAProjectsChangedEvent, getMyPLAAccessRights, hasPLAWriteAccess, isPLANumberValueType } from "./PLAState";
import { getPLAAlerts, resolvePLAElementExpressionAttributes, validatePLAData } from "./PLAExpression";
import { buildPLAZoomFeature, removePLAMapLayers, setPLASelectedFeature, setupPLAMapLayers } from "./PLAMapLayers";
import { PLAElementDialog, createEmptyPLAElement, PLARelationDialog } from "./PLAElementDialog";
import { PLAAlertMark } from "./PLAAlertMark";
import { PLATable, PLATableRef } from "./PLATable";
import { PLAElementTreeDiagram } from "./PLAElementTreeDiagram";
import { createPLAFreeAreaReport, createPLAProjectGroupedRelationReport, createPLAProjectRelationReport } from "./PLAReport";
import { createPLAOptimizationResultAsync, PLAOptimizationResult, PLAOptimizerAlgorithm, PLAOptimizerGoal, PLAOptimizerProposal } from "./PLAOptimizer";
import { formatPLANumber } from "./PLAFormatting";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import "./PLAWindow.css";

type ElementTypeMapSelectionMode = "single" | "multiple";
type OptimizerTargetOption = { value: string; text: string };

const PLALocalStorageMapSelectionModeKey = "PLAState-mapElementTypeSelectionMode";
const PLALocalStorageShowPolygonNamesKey = "PLAState-showPolygonNames";
const PLALocalStorageVisibleElementTypesKey = (projectId: string) => `PLAState-visibleElementTypes-${projectId}`;
const PLALocalStorageSelectedElementTypeKey = (projectId: string) => `PLAState-selectedElementType-${projectId}`;
const PLALocalStorageOptimizerTargetAttributeKey = (projectId: string) => `PLAState-optimizerTargetAttribute-${projectId}`;
const PLALocalStorageOptimizerGoalKey = "PLAState-optimizerGoal";
const PLALocalStorageOptimizerAlgorithmKey = "PLAState-optimizerAlgorithm";
const PLAExternalRefreshIntervalMilliseconds = 1000;
const PLAMolbakLogo = {
   src: "/Moelbaklogo-Groen-Stort.png",
   alt: "Mølbak",
   href: "https://molbak.dk/",
   stacked: false
};
const PLAJernbanebyenLogo = {
   src: "/customerLogo/Jernbanebyen.svg",
   alt: "Jernbanebyen",
   href: "https://jernbanebyen.dk/",
   stacked: true
};

function isJernbanebyenProject(project?: PLAProject) {
   return (project?.name || "").trim().toLowerCase().includes("jernbanebyen");
}

function PLAReportsMenu(props: { items: { value: string; text: string }[]; onSelect: (value: string) => void }) {
   const { root } = useFloatingPortalContext();
   const [open, setOpen] = useState(false);
   const { refs, floatingStyles, context } = useFloating({
      whileElementsMounted: autoUpdate,
      open,
      onOpenChange: setOpen,
      placement: "bottom-end",
      strategy: "fixed",
      middleware: [
         offset(6),
         ...(root != null ? floatingBoundaryMiddleware(root) : [flip({ padding: 8 }), shift({ padding: 8 })]),
      ],
   });
   const { getReferenceProps, getFloatingProps } = useInteractions([
      useClick(context),
      useDismiss(context),
   ]);

   return (
      <>
         <button
            type="button"
            className="pla-btn pla-btn--solid pla-btn--menu"
            ref={refs.setReference}
            aria-haspopup="menu"
            aria-expanded={open}
            {...getReferenceProps()}
         >
            {Localization.getText("Reports")}
         </button>
         {open ? (
            <MITFloatingPortal>
               <div
                  ref={refs.setFloating}
                  className="pla-menu__panel"
                  role="menu"
                  style={floatingStyles}
                  {...getFloatingProps()}
               >
                  {props.items.map((item) => (
                     <button
                        key={item.value}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                           setOpen(false);
                           props.onSelect(item.value);
                        }}
                     >
                        {item.text}
                     </button>
                  ))}
               </div>
            </MITFloatingPortal>
         ) : null}
      </>
   );
}

function getStoredBoolean(key: string, defaultValue: boolean) {
   const value = localStorage.getItem(key);
   if (value === "true") return true;
   if (value === "false") return false;
   return defaultValue;
}

function getStoredOptimizerGoal(): PLAOptimizerGoal {
   return localStorage.getItem(PLALocalStorageOptimizerGoalKey) === "maximize" ? "maximize" : "minimize";
}

function getStoredOptimizerAlgorithm(): PLAOptimizerAlgorithm {
   return localStorage.getItem(PLALocalStorageOptimizerAlgorithmKey) === "astar" ? "astar" : "greedy";
}

function getSortedElementTypeIds(project: PLAProject) {
   return Object.entries(project.elementTypes || {}).sort((a, b) => a[1].sortOrder - b[1].sortOrder).map(([elementTypeId]) => elementTypeId);
}

function allElementTypesVisible(project: PLAProject) {
   return getSortedElementTypeIds(project).reduce<Record<string, boolean>>((acc, elementTypeId) => {
      acc[elementTypeId] = true;
      return acc;
   }, {});
}

function singleElementTypeVisible(project: PLAProject, selectedElementTypeId: string | undefined) {
   return getSortedElementTypeIds(project).reduce<Record<string, boolean>>((acc, elementTypeId) => {
      acc[elementTypeId] = elementTypeId === selectedElementTypeId;
      return acc;
   }, {});
}

function normalizeVisibleElementTypes(project: PLAProject, value: any) {
   if (!value || typeof value !== "object") return allElementTypesVisible(project);
   return getSortedElementTypeIds(project).reduce<Record<string, boolean>>((acc, elementTypeId) => {
      acc[elementTypeId] = value[elementTypeId] !== false;
      return acc;
   }, {});
}

function getLowestSortVisibleElementTypeId(project: PLAProject, visibleElementTypes: Record<string, boolean>) {
   return getSortedElementTypeIds(project).find((elementTypeId) => visibleElementTypes[elementTypeId] !== false);
}

function getStoredVisibleElementTypes(project: PLAProject) {
   try {
      return normalizeVisibleElementTypes(project, JSON.parse(localStorage.getItem(PLALocalStorageVisibleElementTypesKey(project.id)) || "null"));
   } catch {
      return allElementTypesVisible(project);
   }
}

function getStoredSelectedElementTypeId(project: PLAProject) {
   const stored = localStorage.getItem(PLALocalStorageSelectedElementTypeKey(project.id)) || undefined;
   return stored && project.elementTypes[stored] ? stored : undefined;
}

function getRecordsSignature(records: PLARecord[]) {
   return records
      .map((record) => `${record.id}:${record._version ?? JSON.stringify(record)}`)
      .sort()
      .join("|");
}

function getRecordSignature(record: PLARecord | undefined) {
   return record ? `${record.id}:${record._version ?? JSON.stringify(record)}` : "";
}

export function PLAWindow(props: { managedWindow: ManagedWindow }) {
   const { state: sessionState } = useContext(SessionContext);
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);

   const { state: windowState, dispatch: windowDispatch } = useWindowContext();
   const { root: floatingPortalRoot } = useFloatingPortalContext();
   const plaPortalRoot = props.managedWindow.asPopop ? floatingPortalRoot : document.getElementById("Mit-NonBlockingOverlay") || floatingPortalRoot;
   const [projects, setProjects] = useState<PLAProject[]>([]);
   const [selectedProjectId, setSelectedProjectId] = useState<string>(localStorage.getItem(PLALocalStorageSelectedProjectKey) || "");
   const [elements, setElements] = useState<PLAElementRecord[]>([]);
   const [relations, setRelations] = useState<PLARelationRecord[]>([]);
   const [loading, setLoading] = useState(false);
   const [activeTab, setActiveTab] = useState<string | undefined>();
   const [visibleElementTypes, setVisibleElementTypes] = useState<Record<string, boolean>>({});
   const [elementTypeMapSelectionMode, setElementTypeMapSelectionMode] = useState<ElementTypeMapSelectionMode>(() => localStorage.getItem(PLALocalStorageMapSelectionModeKey) === "single" ? "single" : "multiple");
   const [lastSelectedElementTypeId, setLastSelectedElementTypeId] = useState<string | undefined>();
   const [selectedElementId, setSelectedElementId] = useState<string | undefined>();
   const [showElementEditor, setShowElementEditor] = useState<PLAElementRecord | undefined>();
   const [showRelationEditor, setShowRelationEditor] = useState<PLAElementRecord | undefined>();
   const [confirmDeleteElement, setConfirmDeleteElement] = useState<PLAElementRecord | undefined>();
   const [showMapSettings, setShowMapSettings] = useState(false);
   const [showOptimizerParameters, setShowOptimizerParameters] = useState(false);
   const [optimizerResult, setOptimizerResult] = useState<PLAOptimizationResult | undefined>();
   const [optimizerRunning, setOptimizerRunning] = useState(false);
   const [optimizerProgress, setOptimizerProgress] = useState("");
   const [optimizerProgressPercent, setOptimizerProgressPercent] = useState(0);
   const [optimizerTargetAttributeId, setOptimizerTargetAttributeId] = useState("");
   const [optimizerGoal, setOptimizerGoal] = useState<PLAOptimizerGoal>(() => getStoredOptimizerGoal());
   const [optimizerAlgorithm, setOptimizerAlgorithm] = useState<PLAOptimizerAlgorithm>(() => getStoredOptimizerAlgorithm());
   const [showPolygonNames, setShowPolygonNames] = useState(() => getStoredBoolean(PLALocalStorageShowPolygonNamesKey, true));
   const persistedElementIds = useRef<Set<string>>(new Set());
   const persistedRelationIds = useRef<Set<string>>(new Set());
   const refreshInProgress = useRef(false);
   const localPersistenceInProgress = useRef(false);
   const loadedRecordsSignature = useRef("");
   const tableRefs = useRef<Record<string, PLATableRef | null>>({});
   const selectedProject = projects.find((project) => project.id === selectedProjectId);
   const mapFacade = mapitState.map;

   const availableLayers = useMemo(() => Object.values(mapitState.layers || {})
      .filter((layer: LayerInfo) => [LayerType.GeoJSON_Point, LayerType.GeoJSON_Polygon, LayerType.GeoJSON].includes(layer.type))
      .sort((a: LayerInfo, b: LayerInfo) => String(a.datasetname || a.filename || a.layerId).localeCompare(String(b.datasetname || b.filename || b.layerId))), [mapitState.layers]);
   const writable = hasPLAWriteAccess(selectedProject, sessionState.userRef, sessionState.customerRef) || hasAccessToFeature(Feature.PLAToolWriteAccess);
   const readOnly = !writable;
   const mapElements = useMemo(() => elements.filter((element) => visibleElementTypes[element.elementType] !== false), [elements, visibleElementTypes]);
   const optimizerTargetOptions = useMemo<OptimizerTargetOption[]>(() => {
      if (!selectedProject) return [];
      const seen = new Set<string>();
      return Object.entries(selectedProject.elementTypes || {}).filter(([, candidateElementType]) => candidateElementType.sortOrder === 1).reduce<OptimizerTargetOption[]>((options, [elementTypeId, projectElementType]) => {
         Object.entries(projectElementType?.attributes || {}).forEach(([attributeId, definition]) => {
            if (!isPLANumberValueType(definition.valueType) || seen.has(attributeId)) return;
            seen.add(attributeId);
            options.push({ value: attributeId, text: `${projectElementType?.name || elementTypeId}: ${definition.name || attributeId}` });
         });
         return options;
      }, []);
   }, [selectedProject]);
   const defaultOptimizerTargetAttributeId = optimizerTargetOptions.find((option) => option.value === "VærdiAfUudnyttedeByggeretter")?.value || optimizerTargetOptions[0]?.value || "";

   const projectService = () => new PLAProjectServiceImpl(SettingsManager.getSystemSetting("pla.projectsElasticIndex", ""), sessionState.userName || sessionState.userRef);
   const dataService = (index: string) => new PLADataServiceImpl(index, sessionState.userName || sessionState.userRef);

   const resolveElementsForRecords = useCallback((nextElements: PLAElementRecord[], nextRelations: PLARelationRecord[]) => {
      if (!selectedProject) return nextElements;
      return nextElements.map((element) => resolvePLAElementExpressionAttributes(element, selectedProject, nextElements, nextRelations, { includeErrors: true }));
   }, [selectedProject]);

   const applyRemoteRecords = useCallback((records: PLARecord[]) => {
      if (!selectedProject) return;
      const nextSignature = getRecordsSignature(records);
      if (nextSignature === loadedRecordsSignature.current) return;
      loadedRecordsSignature.current = nextSignature;
      const nextElements = records.filter((record): record is PLAElementRecord => record.recordType === "ELEMENT");
      const nextRelations = records.filter((record): record is PLARelationRecord => record.recordType === "RELATION");
      const resolvedElements = resolveElementsForRecords(nextElements, nextRelations);
      setElements(resolvedElements);
      setRelations(nextRelations);
      persistedElementIds.current = new Set(resolvedElements.map((element) => element.id));
      persistedRelationIds.current = new Set(nextRelations.map((relation) => relation.id));
      setShowElementEditor((current) => {
         const refreshedElement = current ? resolvedElements.find((element) => element.id === current.id) : undefined;
         return refreshedElement && getRecordSignature(refreshedElement) !== getRecordSignature(current) ? refreshedElement : current;
      });
      setShowRelationEditor((current) => {
         const refreshedElement = current ? resolvedElements.find((element) => element.id === current.id) : undefined;
         return refreshedElement && getRecordSignature(refreshedElement) !== getRecordSignature(current) ? refreshedElement : current;
      });
      setConfirmDeleteElement((current) => {
         const refreshedElement = current ? resolvedElements.find((element) => element.id === current.id) : undefined;
         return refreshedElement && getRecordSignature(refreshedElement) !== getRecordSignature(current) ? refreshedElement : current;
      });
   }, [selectedProject, resolveElementsForRecords]);

   const applyRemoteRecord = useCallback((record: PLARecord) => {
      const nextElements = record.recordType === "ELEMENT"
         ? (elements.some((element) => element.id === record.id) ? elements.map((element) => element.id === record.id ? record : element) : [...elements, record])
         : elements;
      const nextRelations = record.recordType === "RELATION"
         ? (relations.some((relation) => relation.id === record.id) ? relations.map((relation) => relation.id === record.id ? record : relation) : [...relations, record])
         : relations;
      applyRemoteRecords([...nextElements, ...nextRelations]);
   }, [applyRemoteRecords, elements, relations]);

   const loadProjectRecords = useCallback(async () => {
      if (!selectedProject) return;
      const records = await dataService(selectedProject.esIndexName).loadRecords();
      applyRemoteRecords(records);
   }, [applyRemoteRecords, selectedProject?.esIndexName, sessionState.userName, sessionState.userRef]);

   const loadProjects = useCallback(() => {
      projectService().loadProjects()
         .then((loadedProjects) => {
            const visibleProjects = loadedProjects.filter((project) => {
               if (hasAccessToFeature(Feature.PLAToolGlobalAdmin)) return true;
               return getMyPLAAccessRights(project, sessionState.userRef, sessionState.customerRef).length > 0;
            });
            setProjects(visibleProjects);
            if (!selectedProjectId && visibleProjects[0]) setSelectedProjectId(visibleProjects[0].id);
         })
         .catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to load projects")}: ${error.message || error}`)));
   }, [appMessageDispatch, hasAccessToFeature, selectedProjectId, sessionState.customerRef, sessionState.userName, sessionState.userRef]);

   useEffect(() => {
      mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Override));

      return () => {
         mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Normal));
      };
   }, []);

   useEffect(() => {
      loadProjects();
   }, [loadProjects]);

   useEffect(() => {
      globalThis.addEventListener?.(PLAProjectsChangedEvent, loadProjects);
      return () => globalThis.removeEventListener?.(PLAProjectsChangedEvent, loadProjects);
   }, [loadProjects]);

   useEffect(() => {
      if (!selectedProject) return;
      localStorage.setItem(PLALocalStorageSelectedProjectKey, selectedProject.id);
      setLoading(true);
      dataService(selectedProject.esIndexName).loadRecords()
         .then((records) => {
            applyRemoteRecords(records);
            const firstType = getSortedElementTypeIds(selectedProject)[0];
            const storedVisibleElementTypes = getStoredVisibleElementTypes(selectedProject);
            const storedSelectedElementTypeId = getStoredSelectedElementTypeId(selectedProject);
            const selectedElementTypeId = storedSelectedElementTypeId || getLowestSortVisibleElementTypeId(selectedProject, storedVisibleElementTypes) || firstType;
            setActiveTab(firstType);
            setLastSelectedElementTypeId(selectedElementTypeId);
            setVisibleElementTypes(elementTypeMapSelectionMode === "single" ? singleElementTypeVisible(selectedProject, selectedElementTypeId) : storedVisibleElementTypes);
         })
         .catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to load project data")}: ${error.message || error}`)))
         .finally(() => setLoading(false));
   }, [applyRemoteRecords, selectedProject?.id]);

   useEffect(() => {
      if (!selectedProject) return;
      const refresh = () => {
         if (refreshInProgress.current || localPersistenceInProgress.current) return;
         refreshInProgress.current = true;
         loadProjectRecords()
            .catch(() => undefined)
            .finally(() => { refreshInProgress.current = false; });
      };
      const intervalId = window.setInterval(refresh, PLAExternalRefreshIntervalMilliseconds);
      return () => window.clearInterval(intervalId);
   }, [loadProjectRecords, selectedProject?.id]);

   useEffect(() => {
      localStorage.setItem(PLALocalStorageMapSelectionModeKey, elementTypeMapSelectionMode);
   }, [elementTypeMapSelectionMode]);

   useEffect(() => {
      localStorage.setItem(PLALocalStorageShowPolygonNamesKey, String(showPolygonNames));
   }, [showPolygonNames]);

   useEffect(() => {
      if (!selectedProject) return;
      localStorage.setItem(PLALocalStorageVisibleElementTypesKey(selectedProject.id), JSON.stringify(visibleElementTypes));
   }, [selectedProject?.id, visibleElementTypes]);

   useEffect(() => {
      if (!selectedProject || !lastSelectedElementTypeId) return;
      localStorage.setItem(PLALocalStorageSelectedElementTypeKey(selectedProject.id), lastSelectedElementTypeId);
   }, [selectedProject?.id, lastSelectedElementTypeId]);

   useEffect(() => {
      if (!selectedProject) return;
      const storedTargetAttributeId = localStorage.getItem(PLALocalStorageOptimizerTargetAttributeKey(selectedProject.id)) || "";
      const validStoredTargetAttributeId = optimizerTargetOptions.some((option) => option.value === storedTargetAttributeId) ? storedTargetAttributeId : "";
      setOptimizerTargetAttributeId(validStoredTargetAttributeId || defaultOptimizerTargetAttributeId);
   }, [selectedProject?.id, defaultOptimizerTargetAttributeId, optimizerTargetOptions]);

   useEffect(() => {
      localStorage.setItem(PLALocalStorageOptimizerGoalKey, optimizerGoal);
   }, [optimizerGoal]);

   useEffect(() => {
      localStorage.setItem(PLALocalStorageOptimizerAlgorithmKey, optimizerAlgorithm);
   }, [optimizerAlgorithm]);

   useEffect(() => {
      if (!selectedProject || !optimizerTargetAttributeId) return;
      localStorage.setItem(PLALocalStorageOptimizerTargetAttributeKey(selectedProject.id), optimizerTargetAttributeId);
   }, [selectedProject?.id, optimizerTargetAttributeId]);

   const alertsByElementId = useMemo(() => {
      if (!selectedProject) return {};
      return elements.reduce<Record<string, string[]>>((acc, element) => {
         acc[element.id] = getPLAAlerts(element, selectedProject, elements, relations);
         return acc;
      }, {});
   }, [selectedProject, elements, relations]);

   const alertMessagesByElementType = useMemo(() => {
      return elements.reduce<Record<string, string[]>>((acc, element) => {
         const messages = (alertsByElementId[element.id] || []).map((message) => element.name ? `${element.name}: ${message}` : message);
         if (!messages.length) return acc;
         acc[element.elementType] = [...(acc[element.elementType] || []), ...messages];
         return acc;
      }, {});
   }, [elements, alertsByElementId]);

   useEffect(() => {
      const map = mapFacade?.getMapPop?.();
      if (!map || !selectedProject) return;
      const cleanup = setupPLAMapLayers(map, mapElements, alertsByElementId, {
         onFeatureClick: (elementId) => {
            const element = elements.find((item) => item.id === elementId);
            if (!element) return;
            setPLASelectedFeature(map, elementId);
            setSelectedElementId(elementId);
            setActiveTab(element.elementType);
            window.setTimeout(() => tableRefs.current[element.elementType]?.scrollToRow(elementId, true), 0);
         }
      }, selectedProject.elementTypes || {}, { showPolygonLabels: showPolygonNames });
      return cleanup;
   }, [mapFacade, elements, mapElements, alertsByElementId, selectedProject, showPolygonNames]);

   useEffect(() => {
      return () => removePLAMapLayers(mapFacade?.getMapPop?.());
   }, [mapFacade]);

   const zoomToElement = useCallback((element: PLAElementRecord) => {
      setSelectedElementId(element.id);
      const map = mapFacade?.getMapPop?.();
      if (map) setPLASelectedFeature(map, element.id);
      const feature = buildPLAZoomFeature(element);
      if (feature) mapFacade?.zoomToDataAvoidingWindows?.(feature, 25);
   }, [mapFacade]);

   const navigateToElement = useCallback((elementId: string) => {
      const element = elements.find((item) => item.id === elementId);
      if (!element) return;
      setActiveTab(element.elementType);
      zoomToElement(element);
      window.setTimeout(() => tableRefs.current[element.elementType]?.scrollToRow(elementId, true), 0);
   }, [elements, zoomToElement]);

   const elementTreeWindowParams = useMemo<PLAElementTreeDiagramWindowParams | undefined>(() => {
      if (!selectedProject) return undefined;
      return { project: selectedProject, elements, relations, onElementClick: navigateToElement };
   }, [selectedProject, elements, relations, navigateToElement]);

   const elementTreeWindow = windowState.find((item) => item.id === WindowId.PLAElementTreeDiagram);

   useEffect(() => {
      if (elementTreeWindow?.state !== WindowState.Open || !elementTreeWindowParams) return;
      windowDispatch(sendParams(WindowId.PLAElementTreeDiagram, elementTreeWindowParams));
   }, [elementTreeWindow?.state, elementTreeWindowParams, windowDispatch]);

   const showSaveError = async (error: any) => {
      appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to save changes")}: ${error.message || error}`));
      if (error instanceof OptimisticLockingConflictError) {
         const record = error.currentDocument as PLARecord | undefined;
         if (record?.recordType === "ELEMENT" || record?.recordType === "RELATION") applyRemoteRecord(record);
         await loadProjectRecords().catch(() => undefined);
      }
   };

   const resolveElementsForState = (nextElements: PLAElementRecord[], nextRelations: PLARelationRecord[]) => {
      return resolveElementsForRecords(nextElements, nextRelations);
   };

   const validateNextState = (nextElements: PLAElementRecord[], nextRelations: PLARelationRecord[]) => {
      if (!selectedProject) return false;
      const validationErrors = validatePLAData(selectedProject, nextElements, nextRelations);
      if (validationErrors.length > 0) {
         appMessageDispatch(actionSetErrorMessage(validationErrors.join("\n")));
         return false;
      }
      return true;
   };

   const persistElementChanges = async (nextElements: PLAElementRecord[], nextRelations: PLARelationRecord[], elementIdsToSave: string[]) => {
      if (!selectedProject || !validateNextState(nextElements, nextRelations)) return false;
      const resolvedElements = resolveElementsForState(nextElements, nextRelations);
      const elementsToSave = resolvedElements.filter((element) => elementIdsToSave.includes(element.id));
      localPersistenceInProgress.current = true;
      try {
         const service = dataService(selectedProject.esIndexName);
         await Promise.all(elementsToSave.map((element) => service.saveElement(element)));
         setElements(resolvedElements);
         elementsToSave.forEach((element) => persistedElementIds.current.add(element.id));
         return true;
      } catch (error: any) {
         await showSaveError(error);
         return false;
      } finally {
         localPersistenceInProgress.current = false;
      }
   };

   const handleClose = () => {
      removePLAMapLayers(mapFacade?.getMapPop?.());
      windowDispatch(closeWindow(WindowId.PLAWindow));
   };

   const saveElementLocal = async (element: PLAElementRecord) => {
      const exists = elements.some((item) => item.id === element.id);
      const nextElements = exists ? elements.map((item) => item.id === element.id ? element : item) : [...elements, element];
      if (await persistElementChanges(nextElements, relations, [element.id])) setShowElementEditor(undefined);
   };

   const deleteRelationPersisted = async (relationId: string) => {
      if (!selectedProject) return;
      const nextRelations = relations.filter((relation) => relation.id !== relationId);
      if (!validateNextState(elements, nextRelations)) return;
      localPersistenceInProgress.current = true;
      try {
         if (persistedRelationIds.current.has(relationId)) await dataService(selectedProject.esIndexName).deleteRelation(relationId);
         persistedRelationIds.current.delete(relationId);
         setRelations(nextRelations);
         setElements(resolveElementsForState(elements, nextRelations));
      } catch (error: any) {
         await showSaveError(error);
      } finally {
         localPersistenceInProgress.current = false;
      }
   };

   const deleteElementLocal = async (element: PLAElementRecord) => {
      if (!selectedProject) return;
      const relationIdsToDelete = relations.filter((relation) => relation.fromId === element.id || relation.toId === element.id).map((relation) => relation.id);
      const nextElements = elements.filter((item) => item.id !== element.id);
      const nextRelations = relations.filter((relation) => relation.fromId !== element.id && relation.toId !== element.id);
      if (!validateNextState(nextElements, nextRelations)) return;
      localPersistenceInProgress.current = true;
      try {
         const service = dataService(selectedProject.esIndexName);
         await Promise.all([
            ...relationIdsToDelete.filter((relationId) => persistedRelationIds.current.has(relationId)).map((relationId) => service.deleteRelation(relationId)),
            ...(persistedElementIds.current.has(element.id) ? [service.deleteElement(element.id)] : [])
         ]);
         persistedElementIds.current.delete(element.id);
         relationIdsToDelete.forEach((relationId) => persistedRelationIds.current.delete(relationId));
         setElements(resolveElementsForState(nextElements, nextRelations));
         setRelations(nextRelations);
         setConfirmDeleteElement(undefined);
      } catch (error: any) {
         await showSaveError(error);
      } finally {
         localPersistenceInProgress.current = false;
      }
   };

   const selectElementTypeSelectionMode = (mode: ElementTypeMapSelectionMode) => {
      if (!selectedProject) {
         setElementTypeMapSelectionMode(mode);
         return;
      }
      const firstType = getSortedElementTypeIds(selectedProject)[0];
      const fallbackSelectedElementTypeId = lastSelectedElementTypeId && selectedProject.elementTypes[lastSelectedElementTypeId] ? lastSelectedElementTypeId : undefined;
      if (mode === "single") {
         const selectedElementTypeId = getLowestSortVisibleElementTypeId(selectedProject, visibleElementTypes) || fallbackSelectedElementTypeId || firstType;
         setLastSelectedElementTypeId(selectedElementTypeId);
         setVisibleElementTypes(singleElementTypeVisible(selectedProject, selectedElementTypeId));
      } else {
         const selectedElementTypeId = getLowestSortVisibleElementTypeId(selectedProject, visibleElementTypes) || fallbackSelectedElementTypeId || firstType;
         setLastSelectedElementTypeId(selectedElementTypeId);
         setVisibleElementTypes(singleElementTypeVisible(selectedProject, selectedElementTypeId));
      }
      setElementTypeMapSelectionMode(mode);
   };

   const selectVisibleElementType = (elementTypeId: string, checked: boolean) => {
      if (!selectedProject) return;
      if (elementTypeMapSelectionMode === "single") {
         setVisibleElementTypes(singleElementTypeVisible(selectedProject, elementTypeId));
         setLastSelectedElementTypeId(elementTypeId);
         return;
      }
      setVisibleElementTypes((prev) => ({ ...prev, [elementTypeId]: checked }));
      if (checked) setLastSelectedElementTypeId(elementTypeId);
   };

   const runOptimizer = async () => {
      if (!selectedProject || optimizerRunning) return;
      const targetAttributeId = optimizerTargetAttributeId || defaultOptimizerTargetAttributeId;
      if (!targetAttributeId) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No numeric project attributes")));
         return;
      }
      setShowOptimizerParameters(false);
      setOptimizerResult(undefined);
      setOptimizerProgress(Localization.getText("Preparing optimizer"));
      setOptimizerProgressPercent(0);
      setOptimizerRunning(true);
      try {
         const result = await createPLAOptimizationResultAsync(selectedProject, elements, relations, {
            targetAttributeId,
            goal: optimizerGoal,
            algorithm: optimizerAlgorithm,
            changeElementTypeName: "Anvendelse",
            changeAttributeName: "Etageareal",
            onProgress: (progress) => {
               setOptimizerProgress(progress.message);
               setOptimizerProgressPercent(progress.percent);
            }
         });
         setOptimizerResult(result);
      } catch (error: any) {
         appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to run optimizer")}: ${error.message || error}`));
      } finally {
         setOptimizerRunning(false);
         setOptimizerProgress("");
         setOptimizerProgressPercent(0);
      }
   };

   const getOptimizerResultTargetText = (result: PLAOptimizationResult) => optimizerTargetOptions.find((option) => option.value === result.parameters.targetAttributeId)?.text || result.parameters.targetAttributeId;

   const getOptimizerResultGoalText = (goal: PLAOptimizerGoal) => goal === "maximize" ? Localization.getText("Maximize value") : Localization.getText("Minimize value");

   const getOptimizerResultAlgorithmText = (algorithm: PLAOptimizerAlgorithm) => algorithm === "astar" ? Localization.getText("A* (optimized)") : Localization.getText("Greedy (fast)");

   const applyOptimizerProposal = async (proposal: PLAOptimizerProposal) => {
      if (!selectedProject) return;
      const changesByElementId = new Map(proposal.changes.map((change) => [change.anvendelseElementId, change]));
      const nextElements = elements.map((element) => {
         const change = changesByElementId.get(element.id);
         if (!change) return element;
         return {
            ...element,
            attributes: {
               ...element.attributes,
               [change.etagearealAttributeId]: { mode: "constant" as const, valueType: "number" as const, value: change.proposedEtageareal }
            }
         };
      });
      if (await persistElementChanges(nextElements, relations, Array.from(changesByElementId.keys()))) {
         setOptimizerResult(undefined);
         appMessageDispatch(actionSetInfoMessage(Localization.getText("Optimizer proposal applied")));
      }
   };

   const showJernbanebyenLogo = isJernbanebyenProject(selectedProject);

   const sortedElementTypes = selectedProject
      ? Object.entries(selectedProject.elementTypes || {}).sort((a, b) => a[1].sortOrder - b[1].sortOrder)
      : [];

   const renderElementTypeTabTitle = (elementTypeId: string, elementType: PLAProject["elementTypes"][string]) => (
      <>
         <input
            type={elementTypeMapSelectionMode === "single" ? "radio" : "checkbox"}
            className="pla-tab__mapToggle"
            name="pla-map-element-type"
            checked={visibleElementTypes[elementTypeId] !== false}
            aria-label={Localization.getText("Show element type on map")}
            title={Localization.getText("Show element type on map")}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => selectVisibleElementType(elementTypeId, event.target.checked)}
         />
         <span className="pla-tab__swatch" style={{ background: elementType.color || "#2d8f85" }} />
         {elementType.name || elementTypeId}
         <PLAAlertMark messages={alertMessagesByElementType[elementTypeId] || []} />
      </>
   );

   return (
      <AdvancedDragModal
         title={Localization.getText("PlanIT")}
         window={props.managedWindow}
         onClose={handleClose}
         allowUserMiniMize={true}
         allowPopout={true}
         allowFullscreen={true}
         className="pla-window"
         PosDefault={{ top: "12px", left: "calc(100% - 12px)", transform: "translateX(-100%)", width: "1100px", height: "720px" }}
         PositionToSave={{ left: true, top: true, width: true, height: true }}
      >
         <ADModalBody>
            <div className="pla-workspace">
               <div className="pla-hero">
                  <div className="pla-hero__project-picker">
                     <label className="pla-hero__project-label" htmlFor="pla-project-select">{Localization.getText("Choose project")}</label>
                     <select
                        id="pla-project-select"
                        className="pla-select"
                        value={selectedProjectId}
                        onChange={(event) => setSelectedProjectId(event.target.value)}
                     >
                        <option value="">{Localization.getText("Choose project")}</option>
                        {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                     </select>
                     <a
                        className="pla-hero__system-logo-link"
                        href={PLAMolbakLogo.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={PLAMolbakLogo.href}
                     >
                        <img className="pla-hero__system-logo" src={PLAMolbakLogo.src} alt={PLAMolbakLogo.alt} />
                     </a>
                  </div>
                  {selectedProject ? (
                     <div className="pla-hero__toolbar">
                        <div className="pla-hero__identity">
                           {showJernbanebyenLogo ? (
                              <a
                                 className="pla-hero__project-logo-link"
                                 href={PLAJernbanebyenLogo.href}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 title={PLAJernbanebyenLogo.href}
                              >
                                 <img
                                    className="pla-hero__project-logo"
                                    src={PLAJernbanebyenLogo.src}
                                    alt={PLAJernbanebyenLogo.alt}
                                 />
                              </a>
                           ) : null}
                           <div className="pla-hero__identity-text">
                              <h2 className="pla-hero__name">{selectedProject.name}</h2>
                              {selectedProject.description ? <p className="pla-hero__description">{selectedProject.description}</p> : null}
                           </div>
                        </div>
                        <div className="pla-hero__actions">
                           <GlassButton className="pla-btn pla-btn--ghost" title={Localization.getText("Map display settings")} onClick={() => setShowMapSettings(true)}><MIBIcon icon="settings" /></GlassButton>
                           <GlassButton className="pla-btn pla-btn--ghost" disabled={!elementTreeWindowParams} onClick={() => elementTreeWindowParams && windowDispatch(openWindow(WindowId.PLAElementTreeDiagram, elementTreeWindowParams))}>{Localization.getText("Element tree")}</GlassButton>
                           <GlassButton className="pla-btn pla-btn--ghost" onClick={() => undefined}>{Localization.getText("Change log")}</GlassButton>
                           <GlassButton className="pla-btn pla-btn--ghost" onClick={() => undefined}>{Localization.getText("Versionering")}</GlassButton>
                           <GlassButton className="pla-btn pla-btn--ghost" disabled={optimizerRunning} onClick={() => setShowOptimizerParameters(true)}>
                              {optimizerRunning ? <span className="pla-hero-cta__copy"><Spinner animation="border" size="sm" />{Localization.getText("Optimize")}</span> : Localization.getText("Optimize")}
                           </GlassButton>
                           <PLAReportsMenu
                              items={[
                                 { value: "relation", text: Localization.getText("PLA relation report") },
                                 { value: "grouped", text: Localization.getText("PLA grouped relation report") },
                                 { value: "freeArea", text: Localization.getText("Friarealsopgørelse") },
                              ]}
                              onSelect={(value) => {
                                 const createReport = value === "freeArea"
                                    ? () => createPLAFreeAreaReport(selectedProject, elements, relations, sessionState.userName || sessionState.userRef || "")
                                    : () => value === "grouped"
                                       ? createPLAProjectGroupedRelationReport(selectedProject, elements, relations)
                                       : createPLAProjectRelationReport(selectedProject, elements, relations);
                                 createReport()
                                    .catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to create report")}: ${error.message || error}`)));
                              }}
                           />
                        </div>
                     </div>
                  ) : null}
               </div>
               {loading ? <div className="pla-boot"><Spinner /></div> : null}
               {optimizerRunning ? (
                  <div className="pla-progress">
                     <Spinner animation="border" size="sm" />
                     <span>{optimizerProgress || Localization.getText("Optimizer running")}</span>
                     <ProgressBar now={optimizerProgressPercent} />
                  </div>
               ) : null}
               {selectedProject ? (
                  <>
                     <div className="pla-tabs" role="tablist">
                        {sortedElementTypes.map(([elementTypeId, elementType]) => (
                           <div
                              key={elementTypeId}
                              role="tab"
                              tabIndex={0}
                              aria-selected={activeTab === elementTypeId}
                              className={`pla-tab${activeTab === elementTypeId ? " pla-tab--active" : ""}`}
                              onClick={() => setActiveTab(elementTypeId)}
                              onKeyDown={(event) => {
                                 if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    setActiveTab(elementTypeId);
                                 }
                              }}
                           >
                              {renderElementTypeTabTitle(elementTypeId, elementType)}
                           </div>
                        ))}
                     </div>
                     {sortedElementTypes.map(([elementTypeId, elementType]) => (
                        <div
                           key={elementTypeId}
                           role="tabpanel"
                           className={`pla-tab-panel${activeTab === elementTypeId ? " pla-tab-panel--active" : ""}`}
                        >
                           <PLATable
                              ref={(ref) => { tableRefs.current[elementTypeId] = ref; }}
                              project={selectedProject}
                              elementTypeId={elementTypeId}
                              elementType={elementType}
                              elements={elements.filter((element) => element.elementType === elementTypeId)}
                              allElements={elements}
                              relations={relations}
                              selectedElementId={selectedElementId}
                              readOnly={readOnly}
                              onRowClick={zoomToElement}
                              onAddElement={() => setShowElementEditor(createEmptyPLAElement(selectedProject, elementTypeId))}
                              onOpenElement={setShowElementEditor}
                              onCreateRelation={setShowRelationEditor}
                              onDeleteElement={setConfirmDeleteElement}
                           />
                        </div>
                     ))}
                  </>
               ) : null}
            </div>
         </ADModalBody>
         {showElementEditor && selectedProject ? createPortal(
            <PLAElementDialog
               project={selectedProject}
               element={showElementEditor}
               elements={elements}
               relations={relations}
               availableLayers={availableLayers}
               readOnly={readOnly}
               onClose={() => setShowElementEditor(undefined)}
               onSave={saveElementLocal}
               onCreateRelation={setShowRelationEditor}
               onDeleteRelation={deleteRelationPersisted}
            />,
            plaPortalRoot
         ) : null}
         {showRelationEditor && selectedProject ? createPortal(
            <PLARelationDialog
               project={selectedProject}
               sourceElement={showRelationEditor}
               elements={elements}
               onClose={() => setShowRelationEditor(undefined)}
               onSave={async (relation, newElement) => {
                  const nextRelations = [...relations, relation];
                  const nextElements = newElement ? [...elements, newElement] : elements;
                  if (!validateNextState(nextElements, nextRelations)) return;
                  const resolvedElements = resolveElementsForState(nextElements, nextRelations);
                  try {
                     const service = dataService(selectedProject.esIndexName);
                     if (newElement) await service.saveElement(resolvedElements.find((element) => element.id === newElement.id) || newElement);
                     await service.saveRelation(relation);
                     if (newElement) persistedElementIds.current.add(newElement.id);
                     persistedRelationIds.current.add(relation.id);
                     setRelations(nextRelations);
                     setElements(resolvedElements);
                     setShowRelationEditor(undefined);
                  } catch (error: any) {
                     showSaveError(error);
                  }
               }}
            />,
            plaPortalRoot
         ) : null}
         {confirmDeleteElement ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Confirm deletion")} onSubmit={() => deleteElementLocal(confirmDeleteElement)} onCancel={() => setConfirmDeleteElement(undefined)}>
               {Localization.getText("Confirm delete of element {elementId}?").replace("{elementId}", confirmDeleteElement.name)}
            </ConfirmationDialog>,
            plaPortalRoot
         ) : null}
         {showMapSettings ? createPortal(
            <AdvancedDragModal title={Localization.getText("Map display settings")} PosDefault={{ width: "360px", height: "220px", left: "50%", transform: "translateX(-50%)", top: "90px" }} onClose={() => setShowMapSettings(false)}>
               <ADModalBody>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                     <label style={{ display: "inline-flex", alignItems: "center", gap: 6, margin: 0 }}>
                        <input type="checkbox" checked={elementTypeMapSelectionMode === "single"} onChange={(event) => selectElementTypeSelectionMode(event.target.checked ? "single" : "multiple")} />
                        {Localization.getText("Single element type on map")}
                     </label>
                     <label style={{ display: "inline-flex", alignItems: "center", gap: 6, margin: 0 }}>
                        <input type="checkbox" checked={showPolygonNames} onChange={(event) => setShowPolygonNames(event.target.checked)} />
                        {Localization.getText("Show names on polygons")}
                     </label>
                  </div>
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow adjust="end">
                     <GlassButton onClick={() => setShowMapSettings(false)}>{Localization.getText("Close")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
            </AdvancedDragModal>,
            plaPortalRoot
         ) : null}
         {showOptimizerParameters && selectedProject ? createPortal(
            <AdvancedDragModal title={Localization.getText("Optimizer parameters")} PosDefault={{ width: "520px", height: "500px", left: "50%", transform: "translateX(-50%)", top: "80px" }} onClose={() => setShowOptimizerParameters(false)}>
               <ADModalBody>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                     <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <h4 style={{ margin: 0 }}>{Localization.getText("Optimize this value")}</h4>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                           <span>{Localization.getText("Attribute to optimize")}</span>
                           <select value={optimizerTargetAttributeId || defaultOptimizerTargetAttributeId} onChange={(event) => setOptimizerTargetAttributeId(event.target.value)}>
                              {optimizerTargetOptions.map((option) => <option key={option.value} value={option.value}>{option.text}</option>)}
                           </select>
                        </label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                           <span>{Localization.getText("Optimization goal")}</span>
                           <select value={optimizerGoal} onChange={(event) => setOptimizerGoal(event.target.value as PLAOptimizerGoal)}>
                              <option value="minimize">{Localization.getText("Minimize value")}</option>
                              <option value="maximize">{Localization.getText("Maximize value")}</option>
                           </select>
                        </label>
                     </section>
                     <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <h4 style={{ margin: 0 }}>{Localization.getText("By changing")}</h4>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                           <span>{Localization.getText("Attribute type to change")}</span>
                           <select value="Anvendelse" disabled><option value="Anvendelse">Anvendelse</option></select>
                        </label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                           <span>{Localization.getText("Attribute to change")}</span>
                           <select value="Etageareal" disabled><option value="Etageareal">Etageareal</option></select>
                        </label>
                     </section>
                     <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <h4 style={{ margin: 0 }}>{Localization.getText("Optimizer section settings")}</h4>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                           <span>{Localization.getText("Optimizer algorithm")}</span>
                           <select value={optimizerAlgorithm} onChange={(event) => setOptimizerAlgorithm(event.target.value as PLAOptimizerAlgorithm)}>
                              <option value="greedy">{Localization.getText("Greedy (fast)")}</option>
                              <option value="astar">{Localization.getText("A* (optimized)")}</option>
                           </select>
                        </label>
                     </section>
                  </div>
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow>
                     <GlassButton disabled={!optimizerTargetOptions.length || optimizerRunning} onClick={runOptimizer}>{Localization.getText("Start optimization")}</GlassButton>
                     <GlassButton onClick={() => setShowOptimizerParameters(false)}>{Localization.getText("Cancel optimization")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
            </AdvancedDragModal>,
            plaPortalRoot
         ) : null}
         {optimizerResult ? createPortal(
            <AdvancedDragModal title={Localization.getText("PLA optimizer proposals")} PosDefault={{ width: "760px", height: "560px", left: "50%", transform: "translateX(-50%)", top: "90px" }} onClose={() => setOptimizerResult(undefined)}>
               <ADModalBody>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8, marginBottom: 12, padding: 10, border: "1px solid rgba(255,255,255,.25)", borderRadius: 4 }}>
                     <strong style={{ gridColumn: "1 / -1" }}>{Localization.getText("Optimization input parameters")}</strong>
                     <span>{Localization.getText("Attribute to optimize")}: {getOptimizerResultTargetText(optimizerResult)}</span>
                     <span>{Localization.getText("Optimization goal")}: {getOptimizerResultGoalText(optimizerResult.parameters.goal)}</span>
                     <span>{Localization.getText("Optimizer algorithm")}: {getOptimizerResultAlgorithmText(optimizerResult.parameters.algorithm)}</span>
                     <span>{Localization.getText("Attribute type to change")}: {optimizerResult.parameters.changeElementTypeName}</span>
                     <span>{Localization.getText("Attribute to change")}: {optimizerResult.parameters.changeAttributeName}</span>
                  </div>
                  {optimizerResult.proposals.length === 0 ? (
                     <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div>{Localization.getText("No optimizer proposals")}</div>
                        {optimizerResult.warningLimitStats.length ? (
                           <div>
                              <h4 style={{ margin: "4px 0" }}>{Localization.getText("Warnings limiting optimizer")}</h4>
                              <table style={{ width: "100%" }}>
                                 <thead>
                                    <tr>
                                       <th style={{ textAlign: "left" }}>{Localization.getText("Warning")}</th>
                                       <th style={{ textAlign: "left" }}>{Localization.getText("Blocked proposals")}</th>
                                       <th style={{ textAlign: "left" }}>{Localization.getText("Examples")}</th>
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {optimizerResult.warningLimitStats.map((stat) => (
                                       <tr key={stat.id}>
                                          <td>{stat.message}</td>
                                          <td>{stat.count}</td>
                                          <td>{stat.examples.join(", ")}</td>
                                       </tr>
                                    ))}
                                 </tbody>
                              </table>
                           </div>
                        ) : null}
                     </div>
                  ) : (
                     <table style={{ width: "100%" }}>
                        <thead>
                           <tr>
                              <th style={{ textAlign: "left" }}>{Localization.getText("Top-level element")}</th>
                              <th style={{ textAlign: "left" }}>{Localization.getText("Surplus value")}</th>
                              <th style={{ textAlign: "left" }}>{Localization.getText("Changes")}</th>
                              <th style={{ textAlign: "left" }}>{Localization.getText("Proposal")}</th>
                              <th style={{ textAlign: "left" }}>{Localization.getText("Actions")}</th>
                           </tr>
                        </thead>
                        <tbody>
                           {optimizerResult.proposals.map((proposal) => (
                              <tr key={proposal.id}>
                                 <td>{proposal.topElementName}</td>
                                 <td>{formatPLANumber(proposal.currentValue)}</td>
                                 <td>
                                    <table style={{ width: "100%" }}>
                                       <thead>
                                          <tr>
                                             <th style={{ textAlign: "left" }}>{Localization.getText("Byggefelt")}</th>
                                             <th style={{ textAlign: "left" }}>{Localization.getText("Anvendelse")}</th>
                                             <th style={{ textAlign: "left" }}>{Localization.getText("Current Etageareal")}</th>
                                             <th style={{ textAlign: "left" }}>{Localization.getText("Proposed Etageareal")}</th>
                                             <th style={{ textAlign: "left" }}>{Localization.getText("Change")}</th>
                                          </tr>
                                       </thead>
                                       <tbody>
                                          {proposal.changes.map((change) => (
                                             <tr key={change.id}>
                                                <td>{change.byggefeltName || ""}</td>
                                                <td>{change.anvendelseName}</td>
                                                <td>{formatPLANumber(change.currentEtageareal)}</td>
                                                <td>{formatPLANumber(change.proposedEtageareal)}</td>
                                                <td>{formatPLANumber(change.etagearealChange)}</td>
                                             </tr>
                                          ))}
                                       </tbody>
                                    </table>
                                 </td>
                                 <td>
                                    <strong>{proposal.actionText}</strong><br />
                                    <span>{proposal.details}</span>
                                    {proposal.expressionTrace.length ? <details><summary>{Localization.getText("Expression trace")}</summary><ul>{proposal.expressionTrace.map((line) => <li key={line}>{line}</li>)}</ul></details> : null}
                                 </td>
                                 <td><GlassButton onClick={() => applyOptimizerProposal(proposal)}>{Localization.getText("Do it")}</GlassButton></td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  )}
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow>
                     <GlassButton onClick={() => setOptimizerResult(undefined)}>{Localization.getText("Close")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
            </AdvancedDragModal>,
            plaPortalRoot
         ) : null}
      </AdvancedDragModal>
   );
}

export type PLAElementTreeDiagramWindowParams = {
   project: PLAProject;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
   onElementClick: (elementId: string) => void;
};

export function PLAElementTreeDiagramWindow(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const params = props.managedWindow.windowParams as PLAElementTreeDiagramWindowParams | undefined;

   if (!params) return null;

   return (
      <AdvancedDragModal
         title={Localization.getText("Element tree")}
         window={props.managedWindow}
         allowUserMiniMize={true}
         allowPopout={true}
         allowFullscreen={true}
         PosDefault={{ width: "980px", height: "680px", left: "50%", transform: "translateX(-50%)", top: "40px" }}
         PositionToSave={{ left: true, top: true, width: true, height: true }}
         saveKey="PLAElementTreeDiagram"
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
      >
         <ADModalBody>
            <PLAElementTreeDiagram project={params.project} elements={params.elements} relations={params.relations} onElementClick={params.onElementClick} />
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow adjust="end">
               <GlassButton onClick={() => windowDispatch(closeWindow(props.managedWindow.id))}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}
